import { useLocation, useRouter } from "@tanstack/react-router";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { resolveHeaderMascot } from "@/app/layouts/headerMascot";
import {
  createHeaderMediator,
  type HeaderEffect,
  type HeaderInputEvent,
  isHeaderEvent,
} from "@/app/layouts/headerMediator";
import { getInitialHeaderScrollY } from "@/app/layouts/headerScroll";
import { isHistoryPath } from "@/app/layouts/headerSearchVisibility";
import { BOARD_DISPLAY_NAME } from "@/config/browserBoard";
import { useCurrentUrlSlug } from "@/shared/hooks/useCurrentUrlSlug";
import type { EventNode, TypedEvent } from "@/shared/lib/componentHierarchy";
import { useFromMixCatalog } from "@/shared/lib/mixReturn";
import { resolveThreadBoardScope } from "@/shared/lib/threadBoardScope";
import {
  isThreadRoutePath,
  parseMixRouteThreadSlug,
} from "@/shared/lib/threadRoutePath";
import {
  useComponentNode,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";

export interface HeaderViewProps {
  readonly displayName: string;
  readonly mascot: string;
  readonly isThreadView: boolean;
  readonly isSearchAvailable: boolean;
  readonly isSearchOpen: boolean;
  readonly isHeaderVisible: boolean;
  readonly onEvent: (event: HeaderInputEvent) => void;
}

type HeaderRouter = ReturnType<typeof useRouter>;

export function useHeaderPresenter(): HeaderViewProps & {
  node: EventNode<TypedEvent>;
} {
  const location = useLocation();
  const router = useRouter();
  const fromMixCatalog = useFromMixCatalog();
  const isThreadView = isThreadRoutePath(location.pathname);
  const displayName = isThreadView
    ? resolveThreadBoardScope(parseMixRouteThreadSlug(location.pathname))
        .displayName
    : BOARD_DISPLAY_NAME;
  const mascot = resolveHeaderMascot(useCurrentUrlSlug());
  const isSearchAvailable = !isThreadView && !isHistoryPath(location.pathname);
  const dispatchRef = useRef<(event: TypedEvent) => void>(() => {});
  const mediator = useMemo(
    () =>
      createHeaderMediator({
        initialScrollY: getInitialHeaderScrollY(),
        runEffect: (effect: HeaderEffect): void => {
          if (effect.type === "side-menu.open") dispatchRef.current(effect);
          else runHeaderEffect(effect, router);
        },
      }),
    [router],
  );
  useHierarchyLifetime(mediator);

  const node = useComponentNode((event) => {
    if (!isHeaderEvent(event)) return "unhandled";
    const result = mediator.dispatch(event);
    if (result.status === "handled") return "handled";
    // The Header mediator deliberately leaves side-menu.open for its parent.
    return "unhandled";
  });
  const state = useMediatorSnapshot(mediator);
  useLayoutEffect(() => {
    dispatchRef.current = node.dispatch;
  }, [node]);

  useLayoutEffect(() => {
    node.dispatch(
      makeTypedEvent("header.search-availability-changed", {
        isSearchAvailable,
      }),
    );
  }, [isSearchAvailable, node]);

  useEffect((): (() => void) => {
    const handleScroll = (): void => {
      node.dispatch(
        makeTypedEvent("header.scroll", { scrollY: window.scrollY }),
      );
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return (): void => window.removeEventListener("scroll", handleScroll);
  }, [node]);

  const onEvent = useCallback(
    (event: HeaderInputEvent): void => {
      if (event.type === "header.search-toggle") {
        node.dispatch(
          makeTypedEvent("header.search-toggle", { scrollY: window.scrollY }),
        );
        return;
      }
      if (
        event.type === "header.back-requested" ||
        event.type === "header.navigation-requested"
      ) {
        node.dispatch(
          makeTypedEvent(event.type, {
            isThreadView,
            canGoBack: router.history.canGoBack?.() === true,
            fromMixCatalog,
          }),
        );
        return;
      }
      node.dispatch(event);
    },
    [fromMixCatalog, isThreadView, node, router],
  );

  return {
    node,
    displayName,
    mascot,
    isThreadView,
    isSearchAvailable,
    isSearchOpen: state.isSearchOpen,
    isHeaderVisible: state.isHeaderVisible,
    onEvent,
  };
}

function makeTypedEvent<Type extends string, Payload extends object>(
  type: Type,
  payload: Payload,
): TypedEvent {
  return Object.assign({ type }, payload);
}

function runHeaderEffect(effect: HeaderEffect, router: HeaderRouter): void {
  switch (effect.type) {
    case "history.back":
      router.history.back();
      return;
    case "location.assign":
      window.location.assign(effect.href);
      return;
    case "router.navigate-root":
      void router.navigate({ to: "/", replace: true });
      return;
  }
}
